import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Resolve,
  RouterStateSnapshot,
} from '@angular/router';
import { Observable, map, of, BehaviorSubject, tap } from 'rxjs';
import {
  ActionButtonType,
  ClientPageRequest,
  PageRequest,
  Res,
  TogglePayload,
} from 'src/app/shared/models/common';
import { SUPPRESS_ERROR_TOAST } from 'src/app/shared/common/http-context-tokens';
import { CurrencyFormatterService } from 'src/app/shared/services/currency-formatter.service';
import { CountryAddressService } from 'src/app/shared/services/country-address.service';
import { DataService } from 'src/app/shared/services/data.service';
import { UtilityService } from 'src/app/utils/utility.service';
import { environment } from 'src/environments/environment';
import {
  Client,
  ClientBasic,
  ClientBusiness,
  ClientDocuments,
  ClientIdentification,
} from '../models/client';
import { Group } from '../models/group';

interface AccountCount {
  loans: number;
  savings: number;
}

@Injectable({
  providedIn: 'root',
})
export class ClientService {
  private baseUrl: string = environment.baseUrl + environment.coreUrl;
  private baseClientAccountUrl: string =
    environment.baseUrl + environment.accountUrl;
  private bankLogo: string = environment.bankLogoUrl;

  private;

  currentTableRequest: any;
  currentTableList: any[];
  currentItemIndex: any;

  public searchClientEndpoint = `${this.baseUrl}clients?clientType=INDIVIDUAL&page=0&pageSize=500&state=APPROVED&sortField=last_update_time&sort-order=DESC&filter=`;
  public searchClientByParamEndpoint = (param?: string) =>
    `${this.baseUrl}clients?page=0&pageSize=500${
      param ? '&' + param : ''
    }&state=APPROVED&sortField=last_update_time&sortOrder=DESC&filter=`;

  public searchDepositClientByParamEndpoint = (param?: string) =>
    `${this.baseUrl}clients/client-With-Deposit-Account?page=0&pageSize=500${
      param ? '&' + param : ''
    }&sortField=last_update_time&sortOrder=DESC&filter=`;

  private clientAccountCount = new BehaviorSubject<AccountCount>({
    loans: 0,
    savings: 0,
  });

  constructor(
    private http: HttpClient,
    private utilityService: UtilityService,
    private dataService: DataService,
    private currencyFormatter: CurrencyFormatterService,
    private countryAddress: CountryAddressService,
  ) {}

  public getClientAccountCount(): Observable<AccountCount> {
    return this.clientAccountCount.asObservable();
  }
  // From Loan and Deposit Account Service
  public setClientAccountCount(type: 'savings' | 'loans', count: number): void {
    const val = { ...this.clientAccountCount.value, [type]: count };
    this.clientAccountCount.next(val);
  }
  setCurrentTableList(tableList): void {
    this.currentTableList = tableList;
  }

  public getBanks(): Observable<any> {
    return this.http.get(
      this.baseClientAccountUrl + `generic/loan-account/pbanks`,
    );
  }

  public getOrganization(): Observable<any> {
    return this.http.get(this.baseUrl + `franchisees/tenant-and-country`);
  }

  // get savings account
  public getSavingsAccount(
    id: number,
    savingsAccountType?: string,
  ): Observable<any> {
    let params = new HttpParams();

    if (savingsAccountType) {
      params = params.set('savingsAccountType', savingsAccountType);
    }

    return this.http.get(
      this.baseClientAccountUrl + 'saving-accounts/clients/' + id,
      { params },
    );
  }

  setCurrentItemId(item): void {
    for (let index in this.currentTableList) {
      if (this.currentTableList[index].id === item.id) {
        this.currentItemIndex = index;
        return;
      }
    }
  }

  public previousClient(): any {
    let newItemIndex = Number(this.currentItemIndex) - 1;
    let __this = this;

    //check if this is the first item
    if (newItemIndex < 0) {
      //check if we are the very beginning
      if (this.currentTableRequest.start === 0) {
        return new Promise((resolve, reject) => {
          reject('No previous Row');
        });
      }
      this.currentTableRequest.draw = this.currentTableRequest.draw - 1;
      this.currentTableRequest.start =
        this.currentTableRequest.start - this.currentTableRequest.length;

      return new Promise((resolve, reject) => {
        this.getClients(this.currentTableRequest).subscribe((data) => {
          __this.currentItemIndex = this.currentTableRequest.length - 1;
          __this.currentTableList = data.data;

          if (this.currentTableList[__this.currentItemIndex].id) {
            resolve(this.currentTableList[__this.currentItemIndex].id);
          } else {
            reject('No previous rows');
          }
        });
      });
    } else {
      return new Promise((resolve) => {
        resolve(this.currentTableList[newItemIndex].id);
      });
    }
  }

  public nextClient(): Promise<any> {
    let newItemIndex = Number(this.currentItemIndex) + 1;
    let __this = this;

    //check if this is the last item
    if (newItemIndex === this.currentTableList.length) {
      this.currentTableRequest.draw = this.currentTableRequest.draw + 1;
      this.currentTableRequest.start =
        this.currentTableRequest.start + this.currentTableRequest.length;
      return new Promise((resolve, reject) => {
        this.getClients(this.currentTableRequest).subscribe((data) => {
          __this.currentItemIndex = 0;
          __this.currentTableList = data.data;

          if (this.currentTableList[__this.currentItemIndex].id) {
            resolve(this.currentTableList[__this.currentItemIndex].id);
          } else {
            reject('No more rows');
          }
        });
      });
    } else {
      return new Promise((resolve) => {
        resolve(this.currentTableList[newItemIndex].id);
      });
    }
  }

  public isDirectGet() {
    if (this.currentTableList) {
      return false;
    } else {
      return true;
    }
  }

  public isFirstElement() {
    return (
      Number(this.currentTableRequest.start) === 0 &&
      Number(this.currentItemIndex) === 0
    );
  }

  /**
   * The onboarding preview explains every failure itself (what went wrong,
   * which field, what to do next), so the interceptor's generic toast is
   * suppressed here rather than stacking a second, vaguer message on top.
   */
  public postClient(data: Client) {
    return this.http.post<any>(this.baseUrl + 'clients', data, {
      context: new HttpContext().set(SUPPRESS_ERROR_TOAST, true),
    });
  }
  public saveDraft(data: Client) {
    return this.http.post<any>(this.baseUrl + 'clients/draft', data);
  }
  public verifyClient(data: { identifier: string; identifierType?: string; countryCode?: string }): Observable<any> {
    // The interceptor would toast the raw failure text ("Unknown error occurred"); callers show
    // identityVerificationMessage() instead.
    return this.http.post<any>(
      this.baseUrl + 'verification/identity/verify',
      data,
      { headers: { 'suppress-error-toast': 'true' } },
    );
  }

  // public updateBank(data: any) {
  //   if(!data.bvn) return of(null);

  //   return this.http.put<any>(this.baseUrl + 'clients/bank-details', data);
  // }
  public updateClient(data: Object) {
    return this.http.put<any>(this.baseUrl + 'clients/edit', data);
  }
  public getClients(queryParams: PageRequest): Observable<any> {
    // this.currentTableRequest = req;
    const payload = this.utilityService.objToQueryParams(queryParams);
    return this.http.get(`${this.baseUrl}clients${payload}`);
  }
  public getTransferDetailList(req: any): Observable<any> {
    this.currentTableRequest = req;
    return this.dataService.getList(
      this.baseUrl + 'api/v2/internal/core/clients/transfers',
      req,
    );
  }
  public getClient(id: number): Observable<any> {
    return this.http.get(this.baseUrl + 'clients/' + id);
  }
  public getClientInDraft(id: number): Observable<any> {
    return this.http.get(this.baseUrl + 'clients/get-by-id/' + id);
  }
  public clientEditCompare(id: number): Observable<any> {
    return this.http.get(this.baseUrl + 'clients/' + id + '/compare');
  }
  public toggleEditedClient(id, state): Observable<any> {
    return this.http.put(`${this.baseUrl}clients/${id}/approve-edit`, {
      state,
    });
  }

  public checkBvn(bvn: string): Observable<any> {
    return this.http.get(this.baseUrl + 'clients/bvn/' + bvn);
  }
  public getClientByIdentifier(identifier: string): Observable<any> {
    return this.http.get(this.baseUrl + 'clients/identifier/' + identifier);
  }
  public toggle(type: ActionButtonType['value'], data: TogglePayload) {
    if (type === 'reverse') {
      return this.toggleClientsState(data);
    }
    if (type === 'approve') {
      return this.approveClients(data);
    }
    if (type === 'reject') {
      return this.rejectClients(data);
    }
    if (type === 'disable') {
      return this.toggleClients({ ...data, enable: false });
    }
    // enable and activate
    return this.toggleClients({ ...data, enable: true });
  }
  public approveClients(data: TogglePayload) {
    return this.http.post<any>(this.baseUrl + 'clients/approve', data);
  }
  public rejectClients(data: TogglePayload) {
    return this.http.post<any>(this.baseUrl + 'clients/reject', data);
  }
  /* enable / disable one client. */
  public toggleSingleClient(data: any): Observable<any> {
    return this.http.patch<any>(this.baseUrl + 'api/clients/status', data);
  }
  /* enable / disable clients.Takes data{'ids': [1,2,3], 'enable': bool} */
  public toggleClients(data: TogglePayload): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'clients/toggle', data);
  }
  public toggleClientsState(data: TogglePayload): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'clients/toggle-state', data);
  }
  public toggleCanAllowMultipleLoan(data: TogglePayload): Observable<any> {
    return this.http.post<any>(
      this.baseUrl + 'clients/toggleMultipleLoans',
      data,
    );
  }

  public getClientDocuments(id: string) {
    return this.http.get<any>(this.baseUrl + `clients/${id}/documents`);
  }
  public getClientComments(id: string, request: string) {
    return this.http.get<any>(
      this.baseUrl + `clients/${id}/comments?${request}`,
    );
  }
  public addClientComment(payload: any) {
    return this.http.post<any>(this.baseUrl + `clients/comments`, payload);
  }
  public checkPhoneNumberExists(phoneNumber: string): Observable<Res> {
    return this.http.get<Res>(
      this.baseUrl +
        `clients/check-phone-non-exists?phoneNumber=${phoneNumber}`,
    );
  }
  public getSimilarPersons(req: any): Observable<any> {
    return this.http.post<any>(
      this.baseUrl + `api/clients/check-for-similar-names`,
      req,
    );
  }

  public postClientsTransferRequest(req: any): Observable<any> {
    return this.http.post<any>(
      this.baseUrl + `api/v2/internal/core/clients/transfers`,
      req,
    );
  }

  public toggleTransferRequests(req: any): Observable<any> {
    return this.http.post<any>(
      this.baseUrl + `api/v2/internal/core/clients/transfers/toggle`,
      req,
    );
  }

  /** Check if the id combination supplied already exists at the backend
   *
   * @param clientIdNumber the id number of the specified identification
   * @param id the identification type id
   * @returns {Observable<Object>} contains a code representing either success or error and also a message
   */

  public checkIdNumber(clientIdNumber: string) {
    return this.http.get<any>(
      this.baseUrl + 'api/clients/check-for-idNo/' + clientIdNumber,
    );
  }

  /** This adds a new document to a client
   *
   * @param newDocumentForm
   */
  public addDocument(newDocumentForm: Object) {
    return this.http.post<any>(
      this.baseUrl + `clients/documents`,
      newDocumentForm,
    );
  }

  /** delete document attached to a client
   *
   * @param documentId
   */
  // public deleteDocument(documentId: number[]) {
  //   return this.http.post<any>(
  //     this.baseUrl + `clients/removeDocument`,
  //     documentId
  //   );
  // }

  /** This adds a new identification to a client
   *
   * @param newIdentificationForm
   */
  public addIdentification(id: number, newIdentificationForm: Object) {
    return this.http.post<any>(
      this.baseUrl + `api/clients/identifications/${id}`,
      newIdentificationForm,
    );
  }

  public getIndividualClientsForLoanOfficer(id: number) {
    return this.http.get<any>(
      `${this.baseUrl}api/v2/internal/core/clients/individualClientsForLoanOfficer/${id}`,
    );
  }

  public getUserClients(id: number) {
    return this.http.get<any>(
      `${this.baseUrl}api/v2/internal/core/clients/userClients/${id}`,
    );
  }

  private evalBasicProps(basic: any): any {
    const groupId = basic.clientGroupId as Group;

    const evalBasic: any = {
      ...basic,
      clientGroupId: groupId?.id ?? basic.clientGroupId,
      dateOfBirth: this.formatBusinessDate(basic.dateOfBirth),
      expiryDate: this.formatBusinessDate(basic.expiryDate),
      expiryDate2: this.formatBusinessDate(basic.expiryDate2),
      // The amount input keeps its thousands separators, so a path that skips the step form's own
      // sanitising can leave "50,0000" here. The backend needs a number, so strip the separators.
      ...(basic.loanLimit !== undefined && basic.loanLimit !== null && basic.loanLimit !== ''
        ? { loanLimit: this.utilityService.sanitizeCurrency(basic.loanLimit) }
        : {}),
    };

    if (evalBasic.clientType === 'INDIVIDUAL') {
      delete evalBasic.clientGroupId;
      delete evalBasic.clientGroupRole;
    }
    delete evalBasic.loanOfficer;

    // Drop optional date fields when empty so backend doesn't see "" for
    // a typed date column.
    (['expiryDate2'] as const).forEach((k) => {
      const v = evalBasic[k];
      if (v === null || v === undefined || v === '') {
        delete evalBasic[k];
      }
    });

    return evalBasic;
  }
  private evalIdentificationProps(
    identification: ClientIdentification,
  ): ClientIdentification | {} {
    if (!identification) return {};
    const clientIdPhoto = identification.clientIdPhoto.base64;
    const evalidentification: ClientIdentification = {
      ...identification,
      clientIdPhoto,
    };
    return evalidentification;
  }
  private evalDocumentProps(documents: ClientDocuments): ClientDocuments | {} {
    if (!documents) return {};
    const clientSignaturePhoto = documents.clientSignaturePhoto.base64;

    const evalDocuments: ClientDocuments = {
      ...documents,
      clientSignaturePhoto,
    };
    return evalDocuments;
  }

  private evalBusinessProps(business: any): any {
    if (!business) return {};
    const result: any = {
      ...business,
      businessStartDate: this.formatBusinessDate(business.businessStartDate),
      businessPhone: this.normalizePhone(business.businessPhone),
    };
    this.mapSelfEmployedBusinessFields(result, business);
    if (
      result.businessPhone === null ||
      result.businessPhone === undefined ||
      result.businessPhone === ''
    ) {
      delete result.businessPhone;
    }
    return result;
  }

  /**
   * For self-employed clients the form's "Business Type"/"Business Name" fields
   * are bound to the industry/employerName controls, but the backend stores them
   * in the business-model columns (businessDetailTypeId / businessDetailName), not
   * the employment `industry` text column. Map them across so a save writes the
   * right columns; leave the employed path (industry as a string enum) untouched.
   */
  private mapSelfEmployedBusinessFields(result: any, business: any): void {
    if (business?.employmentStatus !== 'SELF_EMPLOYED') return;
    if (
      business.industry !== null &&
      business.industry !== undefined &&
      business.industry !== ''
    ) {
      result.businessDetailTypeId = Number(business.industry);
      // industry here holds the business-type id, not an employment industry —
      // don't also write it to the employment industry text column.
      delete result.industry;
    }
    if (business.employerName) {
      result.businessDetailName = business.employerName;
    }
  }

  private formatBusinessDate(value: any): any {
    if (!value) return value;
    if (value._d) {
      return this.utilityService.formatDate_custom_yearFirst(value._d);
    }
    if (typeof value === 'object') {
      return this.utilityService.formatDate_custom_yearFirst(value);
    }
    return value;
  }

  private normalizePhone(value: any): any {
    return this.utilityService.toE164ForCountry(
      value,
      this.currencyFormatter.tenantCountryISO,
    );
  }
  public extractClientData(completed: any) {
    const payload: Client | any = {
      ...this.evalBasicProps(completed.basic),
      // ...completed.contact,
      // ...this.evalIdentificationProps(completed.identification),
      // ...this.evalDocumentProps(completed.documents),
      // ...completed.bank,
      ...this.evalBusinessProps(completed.business),
      // ...completed.nextOfKin,
    };
    return this.withFullGeo(payload);
  }

  /**
   * Send every address with its full geography (uuid + code + name for
   * country, state and LGA), which core stores so view/edit can pre-select.
   */
  public withFullGeo(payload: any) {
    (
      ['address', 'nextOfKinAddress', 'businessAddress', 'businessDetailAddress'] as const
    ).forEach((key) => {
      if (payload?.[key]) {
        payload[key] = this.countryAddress.withFullGeo(payload[key]);
      }
    });
    return payload;
  }

  public extractClientDataForDraft(completed: any) {
    const payload: Partial<Client> | any = {
      ...(completed.basic ? this.evalBasicPropsForDraft(completed.basic) : {}),
      ...(completed.business
        ? this.evalBusinessPropsForDraft(completed.business)
        : {}),
    };

    // Add a flag to indicate this is a draft
    payload.isDraft = true;

    return this.withFullGeo(payload);
  }
  private evalBasicPropsForDraft(basic: ClientBasic): Partial<ClientBasic> {
    const groupId = basic?.clientGroupId as Group;
    let dateOfBirth = basic?.dateOfBirth;
    let expiryDate = basic?.expiryDate;
    let phoneNumber = basic?.phoneNumber;
    let nextOfKinPhoneNumber = basic?.nextOfKinPhoneNumber;

    // Handle dates safely
    if (dateOfBirth?._d) {
      dateOfBirth = this.utilityService.formatDate_custom_yearFirst(
        dateOfBirth._d,
      );
    } else if (dateOfBirth && typeof dateOfBirth === 'object') {
      // Handle other date formats
      dateOfBirth =
        this.utilityService.formatDate_custom_yearFirst(dateOfBirth);
    }

    if (expiryDate?._d) {
      expiryDate = this.utilityService.formatDate_custom_yearFirst(
        expiryDate._d,
      );
    } else if (expiryDate && typeof expiryDate === 'object') {
      expiryDate = this.utilityService.formatDate_custom_yearFirst(expiryDate);
    }

    const evalBasic: Partial<ClientBasic> = {
      ...basic,
      clientGroupId: groupId?.id || basic?.clientGroupId,
      dateOfBirth,
      expiryDate,
      // The raw form holds the formatted text ("11,000,000"); the backend expects a number.
      // Saving a draft from a later step re-reads the raw form, so strip the separators here.
      ...(basic?.loanLimit !== undefined && basic?.loanLimit !== null && basic?.loanLimit !== ''
        ? { loanLimit: this.utilityService.sanitizeCurrency(basic.loanLimit) as any }
        : {}),
    };

    // Clean up fields that shouldn't be sent
    if (evalBasic.clientType === 'INDIVIDUAL') {
      delete evalBasic.clientGroupId;
      delete evalBasic.clientGroupRole;
    }

    delete evalBasic.loanOfficer;

    return evalBasic;
  }
  private evalBusinessPropsForDraft(business: any): any {
    if (!business) return {};
    const result: any = {
      ...business,
      businessStartDate: this.formatBusinessDate(business.businessStartDate),
      businessPhone: this.normalizePhone(business.businessPhone),
    };
    this.mapSelfEmployedBusinessFields(result, business);
    return result;
  }
  public getClientAudit(params: any): Observable<any> {
    return this.http.get(`${environment.baseUrl}core/audits`, { params });
  }
  public deleteDocument(ids: number[]): Observable<any> {
    return this.http.post(`${this.baseUrl}clients/remove-bulk-document`, {
      ids: ids,
    });
  }
}
